/* eslint-disable object-curly-newline */
import { useState, useEffect, useMemo, useContext } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import cookie from 'react-cookies'
import { ApplicationContext, APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import Sites from '@sites/index'

const useTagManagerInfo = (options = {}) => {

  const { quoteInfo } = useSelector((state) => state.common.quote)
  const { simType } = useSelector((state) => state.simSelectionPage.selectSimType || {})

  const UUID = 'uuid'
  const [uuid, setUuid] = useState(() => cookie.load(UUID) || null)

  const {
    journeyType = simType || quoteInfo?.SimInformation?.simType || 'NA',
    customFields = {}
  } = options

  // Redux
  const { plan } = useSelector((state) => state.plansPage)
  const { cityName, provinceName } = useSelector((state) => state.phoneNumberPage)
  const { accountInfo, userType, postPaymentError, user } = useSelector((state) => state.common)
  const simError = useSelector((state) => state.simSelectionPage?.simError)
  // const accountSetupPage = useSelector((state) => state.accountSetupPage)
  const phoneNumberPage = useSelector((state) => state.phoneNumberPage)
  const payAndReviewPage = useSelector((state) => state.payAndReviewPage)
  
  // Context
  const {
    [APPLICATION_STATE.IDENTITY]: identity
  } = useContext(ApplicationContext)

  useEffect(() => {
    if (uuid) {
      return
    }
    
    const newUuid = uuidv4()
    cookie.save(UUID, newUuid, { path: '/' })
    setUuid(newUuid)
  }, [uuid])

  // Helper function to collect all error fields
  const collectErrorFields = useMemo(() => {
    const errors = []

    // SimSelection errors
    if (simError) errors.push(simError)

    // // AccountSetup errors
    // if (accountSetupPage?.firstNameError) errors.push(accountSetupPage.firstNameError)
    // if (accountSetupPage?.lastNameError) errors.push(accountSetupPage.lastNameError)
    // if (accountSetupPage?.emailError) errors.push(accountSetupPage.emailError)
    // if (accountSetupPage?.confirmEmailError) errors.push(accountSetupPage.confirmEmailError)
    // if (accountSetupPage?.provinceError) errors.push(accountSetupPage.provinceError)
    // if (accountSetupPage?.postalCodeError) errors.push(accountSetupPage.postalCodeError)
    // if (accountSetupPage?.securityAnswerError) errors.push(accountSetupPage.securityAnswerError)
    // if (accountSetupPage?.securityQuestionError) errors.push(accountSetupPage.securityQuestionError)
    
    // // AccountSetup PIN errors (object)
    // if (accountSetupPage?.pinErrors) {
    //   Object.values(accountSetupPage.pinErrors).forEach(error => {
    //     if (error) errors.push(error)
    //   })
    // }

    // PhoneNumber errors
    if (phoneNumberPage?.phoneNumberError) errors.push(phoneNumberPage.phoneNumberError)
    if (phoneNumberPage?.provinceIdError) errors.push(phoneNumberPage.provinceIdError)
    if (phoneNumberPage?.provinceGroupIdError) errors.push(phoneNumberPage.provinceGroupIdError)
    if (phoneNumberPage?.portInPhoneNumberHasError) errors.push(phoneNumberPage.portInPhoneNumberHasError)
    if (phoneNumberPage?.isPortInHasAgreementError) errors.push(phoneNumberPage.isPortInHasAgreementError)
    if (phoneNumberPage?.alternateContactNumberError) errors.push(phoneNumberPage.alternateContactNumberError)
    
    // PhoneNumber Transfer wireless errors
    if (phoneNumberPage?.selectedSpidError) errors.push(phoneNumberPage.selectedSpidError)
    if (phoneNumberPage?.selectedImeiError) errors.push(phoneNumberPage.selectedImeiError)
    if (phoneNumberPage?.previousProviderAccountNumberError) errors.push(phoneNumberPage.previousProviderAccountNumberError)
    
    // PhoneNumber Transfer wireline errors
    if (phoneNumberPage?.firstNameLandlineError) errors.push(phoneNumberPage.firstNameLandlineError)
    if (phoneNumberPage?.lastNameLandlineError) errors.push(phoneNumberPage.lastNameLandlineError)
    if (phoneNumberPage?.streetNumberError) errors.push(phoneNumberPage.streetNumberError)
    if (phoneNumberPage?.streetNameError) errors.push(phoneNumberPage.streetNameError)
    if (phoneNumberPage?.cityNameError) errors.push(phoneNumberPage.cityNameError)
    if (phoneNumberPage?.selectedProvinceLandlineError) errors.push(phoneNumberPage.selectedProvinceLandlineError)
    if (phoneNumberPage?.postalCodeLandlineError) errors.push(phoneNumberPage.postalCodeLandlineError)
    if (phoneNumberPage?.internetAndTvServicesError) errors.push(phoneNumberPage.internetAndTvServicesError)

    // PayAndReview credit card errors
    if (payAndReviewPage?.creditCardNumberError) errors.push(payAndReviewPage.creditCardNumberError)
    if (payAndReviewPage?.expiryDateError) errors.push(payAndReviewPage.expiryDateError)
    if (payAndReviewPage?.securityCodeError) errors.push(payAndReviewPage.securityCodeError)
    if (payAndReviewPage?.postalCodeError) errors.push(payAndReviewPage.postalCodeError)
    if (payAndReviewPage?.ccApiValidationError) errors.push(payAndReviewPage.ccApiValidationError)
    if (payAndReviewPage?.insufficientAmountError) errors.push(payAndReviewPage.insufficientAmountError)

    // Common errors
    // if (responseError) errors.push(responseError)
    if (postPaymentError) errors.push(postPaymentError)

    // Filter out empty, null, undefined values and return comma-separated string
    return errors.filter(error => error && error.toString().trim() !== '').join(', ') || 'NA'
  }, [
    simError, 
    phoneNumberPage, 
    payAndReviewPage, 
    postPaymentError
  ])

  const tagManagerInfo = useMemo(() => {
    
    const result = {
      uuid: uuid || 'NA',
      plan_name: plan?.sluggedName || customFields?.plan_name || 'NA',
      plan_cost: plan?.value || customFields?.plan_cost || 'NA',
      customer_ban: accountInfo?.ban || 'NA',
      customer_quote_id: accountInfo?.quoteId || quoteInfo?.quoteId || 'NA',
      dealer_info: userType === Sites.dealer ? identity : 'NA',
      journey_type: journeyType || 'NA',
      city: cityName,
      phone_number_province: provinceName,
      field_warning: collectErrorFields,
      // Dealer guest activations only. Read from redux so every page after
      // VerifyPage reports it without wiring anything up; VerifyPage itself
      // overrides it through customFields, since its own events fire before
      // the flag has been recorded. Kept a strict boolean, never 'NA'.
      guestFlow: user?.isGuestFlow === true,
      ...customFields
    }
    
    return result
  }, [uuid, plan, accountInfo, cityName, quoteInfo, provinceName, userType, identity, journeyType, customFields, collectErrorFields, user])

  return tagManagerInfo
}

export default useTagManagerInfo